import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { useQuote } from "@/context/QuoteContext";
import { testIds } from "@/constants/testIds";
import ItemCard from "@/components/ItemCard";
import SEO from "@/components/SEO";
import { Search, Loader2, Calendar, FileDown } from "lucide-react";

const CATEGORIES = [
  { key: "Tutti", testId: testIds.categoryFilterAll },
  { key: "Audio", testId: testIds.categoryFilterAudio },
  { key: "Luci", testId: testIds.categoryFilterLuci },
  { key: "Video", testId: testIds.categoryFilterVideo },
  { key: "DJ & FX", testId: testIds.categoryFilterDj },
  { key: "Strutture & Truss", testId: testIds.categoryFilterStrutture },
];

export default function Catalog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("Tutti");
  const [search, setSearch] = useState("");
  const { startDate, endDate, setStartDate, setEndDate, days } = useQuote();

  useEffect(() => {
    let live = true;
    setLoading(true);
    api
      .get("/items", { params: category !== "Tutti" ? { category } : {} })
      .then((r) => {
        if (live) setItems(r.data);
      })
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [category]);

  const filtered = items.filter(
    (i) =>
      !search.trim() ||
      i.name.toLowerCase().includes(search.toLowerCase()) ||
      i.description.toLowerCase().includes(search.toLowerCase()),
  );

  const hasDates = !!(startDate && endDate);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      <SEO
        title="Catalogo noleggio audio, luci, video e DJ | Spectrum Como e Milano"
        description="Catalogo completo di impianti audio, console DJ Pioneer, luci e videoproiettori a noleggio a Como e Milano. Verifica la disponibilità e chiedi il preventivo."
        canonical="https://www.spectrumservice.it/catalogo"
      />
      <div className="relative overflow-hidden rounded-2xl border border-zinc-800 bg-gradient-to-br from-zinc-900 via-zinc-950 to-black mb-8">
        <div
          className="absolute inset-0 opacity-30 mix-blend-screen"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1563841930606-67e2bce48b78?crop=entropy&cs=srgb&fm=jpg&q=85)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
        <div className="relative px-6 py-10 sm:px-10 sm:py-14 lg:py-20 max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-rose-500/30 bg-rose-500/10 text-rose-300 text-[10px] font-mono uppercase tracking-widest mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
            Catalogo Noleggio 2026
          </div>
          <h1 className="font-heading text-3xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white leading-[0.95]">
            Il tuo evento,
            <br />
            <span className="text-rose-500">illuminato</span> come si deve.
          </h1>
          <p className="mt-4 text-sm sm:text-base text-zinc-400 max-w-lg">
            Audio, luci e video professionali a noleggio. Seleziona le date,
            scegli il materiale e ricevi il tuo preventivo in un attimo.
          </p>
          <a
            data-testid={testIds.catalogSampleBtn}
            href={`${API}/sample-pdf`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 mt-5 min-h-[42px] px-4 rounded-lg bg-zinc-900/80 hover:bg-zinc-800 backdrop-blur border border-rose-500/40 hover:border-rose-500/70 text-rose-300 hover:text-white font-semibold text-xs uppercase tracking-widest transition"
          >
            <FileDown size={14} /> Scarica un esempio di preventivo
          </a>
        </div>
      </div>

      {/* Date range picker */}
      <div className="mb-6 bg-zinc-900/90 border border-zinc-800 rounded-xl p-4">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
          <Calendar size={12} className="text-rose-500" /> Date evento — verifica
          disponibilità
          {hasDates && (
            <span className="ml-auto text-rose-400">
              {days} {days === 1 ? "giorno" : "giorni"}
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input
            data-testid={testIds.catalogDateStart}
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            placeholder="Inizio"
            className="w-full min-h-[44px] px-3 rounded-lg bg-zinc-950 border border-zinc-800 focus:border-rose-600/60 focus:outline-none text-white text-sm"
          />
          <input
            data-testid={testIds.catalogDateEnd}
            type="date"
            value={endDate}
            min={startDate || undefined}
            onChange={(e) => setEndDate(e.target.value)}
            placeholder="Fine"
            className="w-full min-h-[44px] px-3 rounded-lg bg-zinc-950 border border-zinc-800 focus:border-rose-600/60 focus:outline-none text-white text-sm"
          />
        </div>
      </div>

      <div className="flex flex-col gap-3 mb-6">
        <div className="relative">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            data-testid={testIds.catalogSearchInput}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca microfono, testa mobile, proiettore..."
            className="w-full min-h-[44px] pl-10 pr-4 rounded-xl bg-zinc-900 border border-zinc-800 focus:border-rose-600/60 focus:outline-none focus:ring-2 focus:ring-rose-600/20 text-white placeholder-zinc-600 text-sm transition"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap">
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              data-testid={c.testId}
              onClick={() => setCategory(c.key)}
              className={`shrink-0 min-h-[36px] px-3.5 rounded-full text-xs font-semibold uppercase tracking-wider border transition-all ${
                category === c.key
                  ? "bg-rose-600 text-white border-rose-500 shadow-lg shadow-rose-900/40"
                  : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-zinc-700 hover:text-zinc-200"
              }`}
            >
              {c.key}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-500">
          <Loader2 className="animate-spin mr-2" size={20} /> Caricamento...
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-zinc-500 text-sm">
          Nessun articolo trovato in questa categoria.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
          {filtered.map((item) => (
            <ItemCard key={item.id} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}
